import { CheckIcon } from '@chakra-ui/icons';
import { useCategoryOptionColorsResolver } from '@/features/colors/hooks/useCategoryOptionColorsResolver';
import { PALETTE_COLOR_NAMES } from '@/features/colors/palettes';
import {
  Box,
  Flex,
  forwardRef,
  SimpleGrid,
  useRadio,
  useRadioGroup,
  type UseRadioGroupProps,
  type UseRadioProps,
} from '@/ui';

const RadioColor = (props: UseRadioProps): JSX.Element => {
  const { getInputProps, getRadioProps } = useRadio(props);
  const resolveOptionColors = useCategoryOptionColorsResolver();
  // The radio value IS the palette slot; the swatch shows the experience's
  // fill for that slot (palette + colorMode).
  const swatchColors = resolveOptionColors(String(props.value));

  return (
    <Box as="label" bgColor="white" borderRadius="4" cursor="pointer">
      <input {...getInputProps()} />

      <Flex
        {...getRadioProps()}
        align="center"
        bgColor={swatchColors.backgroundColor}
        borderRadius="4"
        borderWidth="1px"
        boxSize="28px"
        justify="center"
        transition="all 0.15s ease"
        _checked={{
          borderColor: 'white',
          boxShadow: `0 0 5px ${swatchColors.backgroundColor}`,
        }}
        _hover={{
          opacity: 0.85,
          borderColor: 'rgba(0, 0, 0, .15)',
          _dark: {
            borderColor: 'rgba(255, 255, 255, .25)',
          },
        }}
      >
        <CheckIcon
          boxSize="12px"
          color={swatchColors.textColor}
          display={props.isChecked ? 'block' : 'none'}
        />
      </Flex>
    </Box>
  );
};

export const ColorInput = forwardRef(
  (props: UseRadioGroupProps, ref): JSX.Element => {
    const { getRootProps, getRadioProps } = useRadioGroup(props);

    return (
      <SimpleGrid
        columns={4}
        display="inline-grid"
        p="1"
        spacing="2"
        {...getRootProps()}
        ref={ref}
      >
        {PALETTE_COLOR_NAMES.map((colorName) => {
          return (
            <RadioColor
              key={colorName}
              {...getRadioProps({ value: colorName })}
            />
          );
        })}
      </SimpleGrid>
    );
  }
);
